import React from "react";
import { motion } from "framer-motion";
import {
  Compass,
  LayoutGrid,
  Clapperboard,
  LineChart,
  ArrowRight,
} from "lucide-react";

const pillars = [
  { letter: "R", name: "Recognize" },
  { letter: "I", name: "Influence" },
  { letter: "M", name: "Multiply" },
];

const steps = [
  {
    n: "01",
    icon: Compass,
    title: "Analisi & Posizionamento",
    desc: "Studiamo brand, mercato, pubblico e competitor per definire identità, tono di voce e direzione creativa.",
  },
  {
    n: "02",
    icon: LayoutGrid,
    title: "Format & Content System",
    desc: "Costruiamo rubriche, idee video, trend adattati al brand e calendario editoriale per contenuti verticali.",
  },
  {
    n: "03",
    icon: Clapperboard,
    title: "Produzione & Editing",
    desc: "Trasformiamo le idee in video con hook, ritmo, sottotitoli, storytelling ed editing più curato della media.",
  },
  {
    n: "04",
    icon: LineChart,
    title: "Ottimizzazione & Crescita Organica",
    desc: "Analizziamo cosa funziona, miglioriamo i format e aumentiamo continuità, copertura e riconoscibilità.",
  },
];

export default function Method() {
  return (
    <section
      id="metodo"
      data-testid="method-section"
      className="relative py-20 md:py-32 bg-rim-surfaceAlt text-rim-text"
    >
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,rgba(124,58,237,0.18),transparent_60%)]" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.05]"
          data-testid="method-title"
        >
          Metodo RIM<span className="text-rim-glow">™</span>
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55, delay: 0.15 }}
          className="mt-6"
          data-testid="method-framework"
        >
          <div className="inline-flex flex-wrap items-center gap-2 sm:gap-3 bg-rim-surface border border-rim/35 rounded-full px-4 sm:px-5 py-2.5">
            {pillars.map((p, i) => (
              <React.Fragment key={p.name}>
                <span className="font-heading text-sm sm:text-base font-semibold tracking-tight text-white">
                  <span className="text-rim-glow">{p.letter}</span>
                  {p.name.slice(1)}
                </span>
                {i < pillars.length - 1 && (
                  <ArrowRight className="h-3.5 w-3.5 text-rim-glow/60" />
                )}
              </React.Fragment>
            ))}
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.22 }}
          className="mt-7 font-heading text-xl md:text-3xl font-bold text-white leading-[1.2] max-w-3xl text-balance"
          data-testid="method-headline"
        >
          Senza metodo, i contenuti restano contenuti.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.32 }}
          className="mt-4 max-w-2xl space-y-3 text-[15px] md:text-base text-rim-muted leading-relaxed"
        >
          <p>
            Il Metodo RIM™ nasce per trasformare idee sparse, video
            improvvisati e profili poco curati in{" "}
            <strong className="font-semibold text-white">
              una presenza digitale riconoscibile, coerente
            </strong>
            .
          </p>
          <p className="font-heading font-bold text-white text-base md:text-lg">
            Perché funziona?
          </p>
          <p>
            Uniamo una{" "}
            <strong className="font-semibold text-white">
              visione nativa dei social
            </strong>{" "}
            a una{" "}
            <strong className="font-semibold text-white">
              strategia concreta
            </strong>
            . Sappiamo cosa funziona oggi e lo replichiamo adattandolo al tuo
            brand e ai tuoi obiettivi.
          </p>
        </motion.div>

        {/* Vertical timeline */}
        <div className="mt-14 md:mt-20 relative">
          <div className="absolute left-[18px] md:left-[22px] top-2 bottom-2 w-px bg-gradient-to-b from-rim/60 via-rim/30 to-transparent" />

          <ol className="space-y-5 md:space-y-7">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.li
                  key={s.n}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  data-testid={`method-step-${s.n}`}
                  className="relative pl-12 md:pl-16"
                >
                  <span className="absolute left-0 top-0 h-9 w-9 md:h-11 md:w-11 rounded-full bg-rim text-white grid place-items-center font-heading font-bold text-sm md:text-base shadow-[0_0_30px_rgba(124,58,237,0.5)]">
                    {s.n}
                  </span>

                  <div className="bg-rim-surface border border-rim/20 rounded-2xl p-5 md:p-6 hover:border-rim/45 hover:scale-[1.01] transition-all duration-300">
                    <div className="flex items-center gap-3 mb-2">
                      <Icon className="h-5 w-5 text-rim-glow" strokeWidth={1.8} />
                      <h3 className="font-heading text-lg md:text-xl font-bold text-white leading-tight">
                        {s.title}
                      </h3>
                    </div>
                    <p className="text-[14px] md:text-[15px] text-rim-muted leading-relaxed">
                      {s.desc}
                    </p>
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
